import {
  ComputedRoute,
  InfoCard,
  RouteReportService,
  Search,
  useGeoveloMap,
  useRouting,
} from '@geovelo-frontends/commons';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormikHelpers, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as Yup from 'yup';

import { AppContext } from '../../context';
import { defaultBounds } from '../../layouts/app-content/map';
import Button from '../button';
import Dialog from '../dialog';
import { InfoIcon } from '../icons';

import { IControl, LngLatBounds } from '!maplibre-gl';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const mapId = 'report-map';

interface IValues {
  email: string;
  description: string;
}

type TProps = Omit<DialogProps, 'onClose'> & { computedRoute: ComputedRoute; onClose: () => void };

function ReportDialog({ computedRoute, onClose, ...props }: TProps): JSX.Element {
  const {
    user: { current: currentUser },
  } = useContext(AppContext);
  const drawControlRef = useRef(
    new MapboxDraw({
      controls: {
        combine_features: false,
        line_string: true,
        point: false,
        polygon: false,
        trash: true,
        uncombine_features: false,
      },
      modes: { ...MapboxDraw.modes, static: StaticMode },
    }),
  );
  const { t } = useTranslation();
  const theme = useTheme();
  const { isSubmitting, values, errors, touched, resetForm, handleChange, handleSubmit } =
    useFormik<IValues>({
      initialValues: { email: currentUser?.email || '', description: '' },
      validationSchema: Yup.object().shape({
        email: Yup.string()
          .email(t('commons.sign_in_form.email_invalid') || '')
          .required(t('commons.sign_in_form.email_required') || ''),
        description: Yup.string().min(10).required(),
      }),
      enableReinitialize: true,
      onSubmit,
    });
  const { enqueueSnackbar } = useSnackbar();
  const {
    initialized: mapInitialized,
    map,
    init: initMap,
    destroy: destroyMap,
  } = useGeoveloMap({
    smallDevice: true,
    baseLayer: 'geovelo',
  });
  const {
    initialized: routingInitialized,
    init: initRouting,
    updateWayPoints: updateWayPointsOnMap,
    showRoutes: showRoutesOnMap,
    destroy: destroyRouting,
  } = useRouting(theme, map, new Search(), {}, { readOnly: true, hidePopup: true }, false);

  useEffect(() => {
    if (props.open) {
      resetForm({ values: { email: currentUser?.email || '', description: '' } });

      setTimeout(() => {
        initMap({
          container: mapId,
          bounds: defaultBounds,
          customZoomControls: true,
          baseLayersControl: true,
        });
      }, theme.transitions.duration.enteringScreen);
    } else {
      destroyRouting();
      destroyMap();
    }
  }, [props.open]);

  useEffect(() => {
    if (map) initRouting();
  }, [map]);

  useEffect(() => {
    if (map && mapInitialized) {
      if (!map.hasControl(drawControlRef.current as unknown as IControl))
        map.addControl(drawControlRef.current as unknown as IControl, 'top-right');

      drawControlRef.current.changeMode('simple_select');
      drawControlRef.current.set({ type: 'FeatureCollection', features: [] });
    }
  }, [mapInitialized, props.open]);

  useEffect(() => {
    if (routingInitialized) {
      const { wayPoints, bounds } = computedRoute;

      updateWayPointsOnMap(wayPoints);
      showRoutesOnMap([computedRoute], 0);

      if (bounds) {
        const { north, east, south, west } = bounds;
        map?.fitBounds(new LngLatBounds({ lat: south, lng: west }, { lat: north, lng: east }), {
          animate: false,
          padding: 50,
        });
      }
    }
  }, [routingInitialized]);

  async function onSubmit(
    { email, description }: IValues,
    { setSubmitting }: FormikHelpers<IValues>,
  ) {
    setSubmitting(true);

    const coordinates: GeoJSON.Position[][] = [];
    drawControlRef.current.getAll().features.forEach(({ geometry }) => {
      if (geometry.type === 'LineString') {
        coordinates.push(geometry.coordinates);
      }
    });

    drawControlRef.current.changeMode('static');
    map?.removeControl(drawControlRef.current as unknown as IControl);

    const geojson: GeoJSON.MultiLineString = { type: 'MultiLineString', coordinates };

    try {
      await RouteReportService.send({
        computed_route_id: computedRoute.id,
        email,
        description,
        geo_report_itinerary: geojson,
      });

      onClose();
      enqueueSnackbar(t('geovelo.routing.report_dialog.sent'));
    } catch (err) {
      enqueueSnackbar(t('geovelo.routing.report_dialog.not_sent'));
    }

    setSubmitting(false);
  }

  return (
    <Dialog
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      maxWidth="md"
      onClose={(_, reason) => (!isSubmitting || reason !== 'backdropClick') && onClose()}
      {...props}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('geovelo.routing.report_dialog.title')}</DialogTitle>
        <StyledMap id={mapId} />
        <StyledDialogContent>
          <StyledInfoCard description={t('geovelo.routing.report_dialog.info')} Icon={InfoIcon} />
          <StyledTextField
            fullWidth
            required
            disabled={isSubmitting}
            error={touched.email && Boolean(errors.email)}
            label={t('commons.email')}
            margin="dense"
            name="email"
            onChange={handleChange}
            rows={3}
            size="small"
            type="email"
            value={values.email}
            variant="outlined"
          />
          <StyledTextField
            fullWidth
            multiline
            required
            disabled={isSubmitting}
            error={touched.description && Boolean(errors.description)}
            id="description"
            inputProps={{ minLength: 10 }}
            label={t('geovelo.routing.report_dialog.description_label')}
            margin="dense"
            name="description"
            onChange={handleChange}
            rows={3}
            value={values.description}
            variant="outlined"
          />
        </StyledDialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={() => onClose()} type="reset">
            {t('commons.actions.cancel')}
          </Button>
          <Button
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress color="inherit" size={16} thickness={4} />}
            type="submit"
            variant="contained"
          >
            {t('geovelo.routing.report_dialog.actions.send')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const StyledMap = styled.div`
  height: 300px;
  position: relative;
`;

const StyledDialogContent = styled(DialogContent)`
  overflow-y: hidden;
  padding: 16px;
`;

const StyledInfoCard = styled(InfoCard)`
  background-color: whitesmoke;
`;

const StyledTextField = styled(TextField)`
  margin-top: 16px;
`;

export default ReportDialog;
